// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pptxCache: any = null;

const SCREENSHOT_LAMBDA_URL = 'https://n0ckdf0gnb.execute-api.us-east-1.amazonaws.com/dev/screenshot';
const S3_DIRECT_UPLOAD_URL = 'https://s3-external-1.amazonaws.com';

async function loadPptxGenJS(): Promise<any> {
  if (pptxCache) return pptxCache;

  return new Promise((resolve, reject) => {
    if ((window as any).PptxGenJS) {
      pptxCache = (window as any).PptxGenJS;
      resolve(pptxCache);
      return;
    }

    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL || ''}/pptxgen.bundle.js`;
    script.onload = () => {
      pptxCache = (window as any).PptxGenJS;
      if (pptxCache) {
        resolve(pptxCache);
      } else {
        reject(new Error('PptxGenJS failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load pptxgen.bundle.js'));
    document.head.appendChild(script);
  });
}

async function getSaveAs() {
  const mod = await import('file-saver');
  return mod.saveAs || (mod as any).default?.saveAs || (mod as any).default;
}

async function captureCreativeViaLambda(
  htmlTag: string,
  size: string,
): Promise<string | null> {
  try {
    // eslint-disable-next-line no-underscore-dangle
    const stage = (window as any).__RAILS_STAGE__ || 'production';

    const response = await fetch(SCREENSHOT_LAMBDA_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        htmlTag,
        size,
        s3DirectUploadUrl: S3_DIRECT_UPLOAD_URL,
        stage,
      }),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      // eslint-disable-next-line no-console
      console.warn('Lambda capture failed:', data.error);
      return null;
    }

    return data.imageUrl;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Lambda capture error:', e);
    return null;
  }
}

/**
 * Capture a creative via the Puppeteer Lambda.
 * Sends the HTML tag to the Lambda, which renders it in headless Chrome
 * (with CORS disabled), takes a screenshot, uploads to S3, and returns the URL.
 */
export async function captureCreativeScreenshot(
  htmlTag: string,
  size: string,
): Promise<string | null> {
  return captureCreativeViaLambda(htmlTag, size);
}

/**
 * Fetch an image URL and convert to base64 data URL.
 * Used to embed Lambda-captured S3 images into PPTX slides.
 */
async function imageUrlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export interface ExportCreative {
  name: string;
  size: string;
  adm?: string; // base64-encoded HTML
  deviceType?: string;
  siteContext?: string;
  screenshot_url?: string; // Pre-captured screenshot URL
}

/**
 * Capture all creatives via Lambda, returning S3 image URLs.
 * Sends the full device mock HTML (frame + site context + ad) to the Lambda.
 */
async function captureAllCreatives(
  creatives: ExportCreative[],
  onProgress?: (completed: number, total: number) => void,
): Promise<Array<{ name: string; size: string; imageUrl: string | null }>> {
  // Lazy-load the mock builder and size profiles
  const [{ buildMockHtml }, { getSizeProfile }] = await Promise.all([
    import('./mockHtmlBuilder'),
    import('./sizeProfiles'),
  ]);

  const results: Array<{ name: string; size: string; imageUrl: string | null }> = [];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < creatives.length; i++) {
    const creative = creatives[i];
    let imageUrl: string | null = creative.screenshot_url || null;

    // If no pre-captured screenshot, capture now via Lambda
    if (!imageUrl && creative.adm) {
      const adHtml = atob(creative.adm);
      const [adW, adH] = creative.size.split('x').map(Number);
      const profile = getSizeProfile(creative.size);

      const deviceType = (creative.deviceType || profile.bestDevice) as any;
      const siteContext = (creative.siteContext || 'blank') as any;

      const mockHtml = buildMockHtml({
        adHtml,
        adWidth: adW || 300,
        adHeight: adH || 250,
        deviceType,
        siteContext,
      });

      const captureSize = `${profile.captureWidth}x${profile.captureHeight}`;
      // eslint-disable-next-line no-await-in-loop
      imageUrl = await captureCreativeViaLambda(mockHtml, captureSize);
    }

    results.push({ name: creative.name, size: creative.size, imageUrl });
    onProgress?.(i + 1, creatives.length);
  }

  return results;
}

export async function downloadSinglePNG(
  creative: ExportCreative,
): Promise<void> {
  const saveAs = await getSaveAs();

  // Use pre-captured screenshot if available
  if (creative.screenshot_url) {
    const response = await fetch(creative.screenshot_url);
    const blob = await response.blob();
    saveAs(blob, `${creative.name}.png`);
    return;
  }

  if (!creative.adm) return;

  const captures = await captureAllCreatives([creative]);
  const cap = captures[0];

  if (cap?.imageUrl) {
    const response = await fetch(cap.imageUrl);
    const blob = await response.blob();
    saveAs(blob, `${creative.name}.png`);
  }
}

export async function downloadMultiplePNG(
  creatives: ExportCreative[],
  onProgress?: (completed: number, total: number) => void,
): Promise<void> {
  const [saveAs, { default: JSZip }] = await Promise.all([
    getSaveAs(),
    import('jszip'),
  ]);
  const zip = new JSZip();

  const captures = await captureAllCreatives(creatives, onProgress);

  // eslint-disable-next-line no-restricted-syntax
  for (const cap of captures) {
    if (cap.imageUrl) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(cap.imageUrl);
        // eslint-disable-next-line no-await-in-loop
        const blob = await response.blob();
        // eslint-disable-next-line no-await-in-loop
        const arrayBuffer = await blob.arrayBuffer();
        zip.file(`${cap.name}.png`, arrayBuffer);
      } catch {
        // Skip failed captures
      }
    }
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  saveAs(blob, 'gallery-previews.zip');
}

export async function downloadPPTX(
  creatives: ExportCreative[],
  onProgress?: (completed: number, total: number) => void,
): Promise<void> {
  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE'; // 13.33 x 7.5 inches

  // Capture all creatives via Lambda
  const captures = await captureAllCreatives(creatives, onProgress);

  // Convert S3 URLs to data URLs for embedding in PPTX.
  // Load images to get actual dimensions for correct aspect ratio.
  const capturesWithData: Array<{ name: string; size: string; dataUrl: string | null; aspectRatio: number }> = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const cap of captures) {
    let dataUrl: string | null = null;
    // Default to mobile device frame aspect ratio
    let aspectRatio = 415 / 707;

    if (cap.imageUrl) {
      try {
        // eslint-disable-next-line no-await-in-loop
        dataUrl = await imageUrlToDataUrl(cap.imageUrl);

        // Get actual image dimensions for correct aspect ratio
        // eslint-disable-next-line no-await-in-loop
        const imgDims = await new Promise<{ w: number; h: number }>((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
          img.onerror = () => resolve({ w: 415, h: 707 });
          img.src = dataUrl!;
        });
        if (imgDims.w && imgDims.h) aspectRatio = imgDims.w / imgDims.h;
      } catch {
        // Failed to fetch — skip
      }
    }

    capturesWithData.push({ name: cap.name, size: cap.size, dataUrl, aspectRatio });
  }

  // Layout: two creatives per slide, side by side
  const slideW = 13.33;
  const slideH = 7.5;
  const margin = 0.4;
  const labelH = 0.6;
  const maxImgH = slideH - margin * 2 - labelH;

  for (let i = 0; i < capturesWithData.length; i += 2) {
    const slide = pptx.addSlide();
    const pair = capturesWithData.slice(i, i + 2);
    const cols = pair.length;
    const colW = (slideW - margin * (cols + 1)) / cols;

    pair.forEach((cap, col) => {
      const x = margin + col * (colW + margin);

      // Fit image within the column while maintaining aspect ratio
      let imgW = colW;
      let imgH = imgW / cap.aspectRatio;
      if (imgH > maxImgH) {
        imgH = maxImgH;
        imgW = imgH * cap.aspectRatio;
      }

      const imgX = x + (colW - imgW) / 2;
      const imgY = margin + labelH;

      // Creative name
      slide.addText(cap.name, {
        x,
        y: margin,
        w: colW,
        h: 0.35,
        fontSize: 11,
        bold: true,
        color: '333333',
        valign: 'top',
      });

      // Size label
      slide.addText(cap.size, {
        x,
        y: margin + 0.3,
        w: colW,
        h: 0.25,
        fontSize: 9,
        color: '888888',
        valign: 'top',
      });

      // Creative screenshot
      if (cap.dataUrl) {
        slide.addImage({
          data: cap.dataUrl,
          x: imgX,
          y: imgY,
          w: imgW,
          h: imgH,
        });
      } else {
        // No capture available — show placeholder text
        slide.addText(`Creative screenshot unavailable\n${cap.size}`, {
          x: imgX,
          y: imgY,
          w: imgW,
          h: imgH,
          fontSize: 10,
          color: '999999',
          align: 'center',
          valign: 'middle',
          fill: { color: 'F0F0F0' },
        });
      }
    });
  }

  const saveAs = await getSaveAs();
  const blob = await pptx.write({ outputType: 'blob' }) as Blob;
  saveAs(blob, 'gallery-previews.pptx');
}
